import {
  getErrorCodeFromUnknown,
  getErrorMessageFromUnknown,
  getErrorTypeFromUnknown,
  isRecord,
} from "@atoms/atom-types";

export type GraphQLErrorLike = Readonly<{
  message?: string;
  path?: readonly (string | number)[];
  locations?: readonly unknown[];
  extensions?: Readonly<Record<string, unknown>>;
}>;

export class GraphQLRequestError extends Error {
  public readonly name = "GraphQLRequestError";
  public readonly operationLabel: string;
  public readonly graphQLErrorCount: number;
  public readonly graphQLPath?: readonly (string | number)[];
  public readonly sentryEventId?: string;
  public readonly type?: string;
  public readonly debugInfos?: Readonly<{ errorCode?: string }>;

  public constructor(
    graphQLErrors: readonly unknown[],
    operationLabel: string,
  ) {
    const primaryError = graphQLErrors[0];
    const message =
      getErrorMessageFromUnknown(primaryError) ??
      `${operationLabel} GraphQL request failed`;

    super(message);
    this.operationLabel = operationLabel;
    this.graphQLErrorCount = graphQLErrors.length;

    const errorCode = getErrorCodeFromUnknown(primaryError);
    const errorType = getErrorTypeFromUnknown(primaryError);
    const sentryEventId = getSentryEventIdFromGraphQLError(primaryError);
    if (errorType) {
      this.type = errorType;
    }
    if (sentryEventId) {
      this.sentryEventId = sentryEventId;
    }
    if (errorCode) {
      this.debugInfos = { errorCode };
    }

    const path = getGraphQLErrorPath(primaryError);
    if (path) {
      this.graphQLPath = path;
    }
  }
}

export function throwIfGraphQLErrors(
  errors: readonly unknown[] | undefined,
  operationLabel: string,
): void {
  if (!errors?.length) return;
  throw new GraphQLRequestError(errors, operationLabel);
}

export function toGraphQLRequestError(
  errors: readonly unknown[],
  operationLabel: string,
): GraphQLRequestError {
  return new GraphQLRequestError(errors, operationLabel);
}

export function isLegacyGraphQLErrorResponseObject(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.message === "string" &&
    ("extensions" in value || "locations" in value || "path" in value)
  );
}

export function getSentryEventIdFromUnknown(
  value: unknown,
): string | undefined {
  if (value instanceof GraphQLRequestError) {
    return value.sentryEventId;
  }
  if (!isRecord(value)) return undefined;

  const directEventId = getSentryEventIdFromGraphQLError(value);
  if (directEventId) return directEventId;

  const graphQLErrors = value.graphQLErrors;
  if (Array.isArray(graphQLErrors)) {
    return graphQLErrors
      .map(getSentryEventIdFromGraphQLError)
      .find((eventId) => eventId !== undefined);
  }

  const errors = value.errors;
  if (Array.isArray(errors)) {
    return errors
      .map(getSentryEventIdFromGraphQLError)
      .find((eventId) => eventId !== undefined);
  }

  return undefined;
}

function getGraphQLErrorPath(
  value: unknown,
): readonly (string | number)[] | undefined {
  if (!isRecord(value) || !Array.isArray(value.path)) {
    return undefined;
  }

  return value.path.filter(
    (segment): segment is string | number =>
      typeof segment === "string" || typeof segment === "number",
  );
}

function getSentryEventIdFromGraphQLError(value: unknown): string | undefined {
  if (!isRecord(value)) return undefined;

  if (typeof value.sentryEventId === "string") {
    return normalizeSentryEventId(value.sentryEventId);
  }

  const extensions = value.extensions;
  if (isRecord(extensions) && typeof extensions.sentryEventId === "string") {
    return normalizeSentryEventId(extensions.sentryEventId);
  }

  return undefined;
}

function normalizeSentryEventId(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}
